import {
  isWorkbookErrorCell,
  type WorkbookDefinition,
  type WorkbookResult,
  type WorkbookRun,
  type WorkbookSinkTargetOption,
} from '@erp/shared-types';
import { sheetFingerprints, type WorkbookPlan } from '@erp/workbooks';
import { workbookRunResults } from './workbookRunResults';

/** Failing rows of a Validation an agent reads: enough to see the pattern. */
const AGENT_VALIDATION_ROWS = 5;
/** Error cells of a preview an agent reads, with their causes: enough to see the pattern. */
const AGENT_ERROR_CELLS = 5;

function itemNames(definition: WorkbookDefinition): Map<string, string> {
  return new Map(
    [...definition.sheets, ...definition.outputs].map((item) => [
      item.id,
      item.name,
    ]),
  );
}

function validationOutcomes(result: WorkbookResult, failedRows: number) {
  return (result.validations ?? []).map((outcome) => ({
    validationId: outcome.validation.id,
    column: outcome.validation.column,
    kind: outcome.validation.kind,
    severity: outcome.validation.severity,
    failedRowCount: outcome.failedRowCount,
    ...(failedRows > 0 && {
      failedRows: outcome.failedRows.slice(0, failedRows),
    }),
  }));
}

function filterCounts(result: WorkbookResult) {
  return (
    result.filterCounts && {
      filterCounts: {
        rowsBeforeFilters: result.filterCounts.inputRowCount,
        removedByFilter: result.filterCounts.filters.map((entry) => ({
          column: entry.filter.column,
          operator: entry.filter.operator,
          removedRowCount: entry.removedRowCount,
        })),
      },
    }
  );
}

/** The first Error cells of a Result's Preview rows, each with its row, column and cause. */
function exampleErrorCells(result: WorkbookResult) {
  const examples: {
    row: number;
    column: string;
    error: string;
    cause?: string;
  }[] = [];
  result.rows.forEach((cells, index) => {
    cells.forEach((cell, column) => {
      if (examples.length >= AGENT_ERROR_CELLS || !isWorkbookErrorCell(cell))
        return;
      examples.push({
        row: result.sample?.rowNumbers[index] ?? index,
        column: result.columns[column] ?? `column ${column + 1}`,
        error: cell.error,
        ...(cell.cause && { cause: cell.cause }),
      });
    });
  });
  return examples;
}

/**
 * A Live preview's Results as an agent reads them: for the target Worksheet
 * and each downstream one it computed, its columns with their Declared types
 * in the planned Definition, its first `rowLimit` Preview rows, row count,
 * Error cells with a few examples, Filter counts and Validation outcomes.
 */
export function agentPreviewResults(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
  results: readonly WorkbookResult[],
  rowLimit: number,
) {
  const names = itemNames(definition);
  return results.map((result) => {
    const declared = plan.sheets.get(result.sheetId)?.output ?? [];
    return {
      sheetId: result.sheetId,
      worksheet: names.get(result.sheetId) ?? 'a removed worksheet',
      columns: result.columns.map((name) => ({
        name,
        type: declared.find((field) => field.name === name)?.type ?? null,
      })),
      rowCount: result.rowCount,
      rows: result.rows.slice(0, rowLimit),
      ...(result.sample && {
        sampledRowNumbers: result.sample.rowNumbers.slice(0, rowLimit),
      }),
      errorCount: result.errorCount ?? 0,
      errorCells: exampleErrorCells(result),
      ...filterCounts(result),
      validations: validationOutcomes(result, AGENT_VALIDATION_ROWS),
    };
  });
}

/**
 * The newest Runs as an agent reads them: what each ran and how it ended,
 * with each Worksheet's row count, error count, Validation failures and
 * whether its Result is still Current for the edited Definition — no rows —
 * plus a Run on sample's report summary and each Run's drift warnings.
 */
export function agentRunSummaries(
  runs: readonly WorkbookRun[],
  definition: WorkbookDefinition,
  limit: number,
) {
  const names = itemNames(definition);
  const fingerprints = sheetFingerprints(definition);
  return runs.slice(0, limit).map((run) => ({
    runId: run.id,
    status: run.status,
    mode: run.mode,
    target: run.targetId ? (names.get(run.targetId) ?? 'a removed item') : null,
    createdAt: run.createdAt,
    origin: run.origin,
    startedBy: run.triggeredBy?.name ?? null,
    error: run.error,
    // A Run on sample's figures are a subset's; drift is warnings, never failures.
    ...agentRunNotes(run),
    results: run.results.map((result) => ({
      sheetId: result.sheetId,
      worksheet: names.get(result.sheetId) ?? 'a removed worksheet',
      rowCount: result.rowCount,
      errorCount: result.errorCount ?? 0,
      current: fingerprints.get(result.sheetId) === result.fingerprint,
      validations: validationOutcomes(result, 0).filter(
        (outcome) => outcome.failedRowCount > 0,
      ),
    })),
  }));
}

/**
 * One Worksheet's Result as its tab chooses it (the newest Current one, else
 * the newest), with its first `rowLimit` rows, Filter counts and each
 * Validation's outcome with a few failing rows. Null when no Run computed it.
 */
export function agentWorksheetResult(
  runs: readonly WorkbookRun[],
  definition: WorkbookDefinition,
  sheetId: string,
  rowLimit: number,
) {
  const chosen = workbookRunResults(runs, sheetFingerprints(definition)).sheet(
    sheetId,
  );
  const { run, result } = chosen;
  if (!run || !result) return null;
  return {
    sheetId,
    runId: run.id,
    ranAt: run.createdAt,
    current: chosen.current,
    columns: result.columns,
    rowCount: result.rowCount,
    rows: result.rows.slice(0, rowLimit),
    ...(result.sample && {
      sampledRowNumbers: result.sample.rowNumbers.slice(0, rowLimit),
    }),
    errorCount: result.errorCount ?? 0,
    ...filterCounts(result),
    validations: validationOutcomes(result, AGENT_VALIDATION_ROWS),
  };
}

/** What a Run tells the agent about its sample and drift, beyond its Results. */
export function agentRunNotes(
  run: Pick<WorkbookRun, 'sampling' | 'comparisons'>,
) {
  const report = run.sampling?.report;
  return {
    ...(report && {
      sample: {
        keys: report.keys,
        distance: report.distance,
        warnings: report.warnings,
      },
    }),
    ...(run.comparisons.some((comparison) => comparison.warnings.length) && {
      drift: run.comparisons
        .filter((comparison) => comparison.warnings.length)
        .map((comparison) => ({
          comparedWith:
            comparison.kind === 'sample'
              ? 'the sample before it'
              : 'the previous run',
          baselineRunId: comparison.baselineRunId,
          warnings: comparison.warnings,
        })),
    }),
  };
}

/** A Stateful table as the agent reads it: what a Data sink Output can target. */
export interface AgentStatefulTable {
  id: string;
  name: string;
  description?: string;
  columns: { name: string; type: string }[];
  keyColumns: string[];
}

/** The Stateful tables among a Data sink's targets, with their columns and key. */
export function agentStatefulTables(
  targets: readonly WorkbookSinkTargetOption[],
): AgentStatefulTable[] {
  return targets.flatMap((target): AgentStatefulTable[] => {
    if (target.kind !== 'stateful-table') return [];
    const columns = target.columns ?? [];
    return [
      {
        id: target.id,
        name: target.label,
        ...(target.description && { description: target.description }),
        columns: columns.map(({ name, type }) => ({ name, type })),
        keyColumns: columns.flatMap((column) =>
          column.isKey ? [column.name] : [],
        ),
      },
    ];
  });
}
